import React, { useState, useEffect } from 'react';
import { X, Save, Code, Database, Edit2, AlertCircle, ArrowRight, Sparkles, Loader, Terminal } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { cn } from '../../lib/utils';
import { udfApi } from '../../api/udfApi';
import { showSuccessToast } from '../../lib/toast';

const FunctionDetailModal = ({ func, isOpen, onClose, onUpdate }) => {
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [isEditingDefinition, setIsEditingDefinition] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');
    const [editedDefinition, setEditedDefinition] = useState('');
    const [fullUdf, setFullUdf] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const isDark = document.documentElement.classList.contains('dark');

    // Fetch full details when opened
    useEffect(() => {
        if (func && isOpen) {
            const fetchFullUdf = async () => {
                setIsLoading(true);
                try {
                    const data = await udfApi.getUDF(func.id);
                    setFullUdf(data);
                    setEditedDescription(data.description || '');
                    setEditedDefinition(data.definition || '');
                } catch (err) {
                    console.error('Failed to fetch UDF details:', err);
                    setFullUdf(func);
                    setEditedDescription(func.description || '');
                    setEditedDefinition('');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchFullUdf();
        } else {
            setFullUdf(null);
            setIsEditingDescription(false);
            setIsEditingDefinition(false);
        }
    }, [func, isOpen]);

    if (!isOpen || !func) return null;

    const displayUdf = fullUdf || func;

    const handleSaveDescription = async () => {
        setIsSaving(true);
        setError(null);

        try {
            await udfApi.updateUDF(func.id, {
                description: editedDescription
            });

            if (onUpdate) onUpdate();
            setIsEditingDescription(false);
            showSuccessToast('Description updated.');
        } catch (err) {
            console.error('Failed to update description:', err);
            setError('Failed to update description.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveDefinition = async () => {
        setIsSaving(true);
        setError(null);

        try {
            await udfApi.updateUDF(func.id, {
                definition: editedDefinition
            });

            if (onUpdate) onUpdate();
            setIsEditingDefinition(false);
            showSuccessToast('Function body updated.');
        } catch (err) {
            console.error('Failed to update definition:', err);
            setError('Failed to update function body.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleRegenerate = async () => {
        setIsRegenerating(true);
        setError(null);

        try {
            const updatedFunc = await udfApi.generateDescription(func.id);
            setEditedDescription(updatedFunc.description);
            if (onUpdate) onUpdate();
            showSuccessToast('Description regenerated.');
        } catch (err) {
            console.error('Failed to regenerate description:', err);
            setError('Failed to generate AI description.');
        } finally {
            setIsRegenerating(false);
        }
    };

    const parseSignature = (sig) => {
        if (!sig) return { name: '', params: [], returnType: '' };

        // Attempt to parse: name(arg1 type1, ...) -> returnType
        const match = sig.match(/^([^(]+)\((.*)\)\s*(?:->\s*(.*))?$/);
        if (!match) return { name: sig, params: [], returnType: 'UNSPECIFIED' };

        const [, name, paramsStr, returnType] = match;
        const params = paramsStr.split(',').map(p => {
            const trimmed = p.trim();
            // Support both "name: TYPE" and "name TYPE" formats
            const colonMatch = trimmed.match(/^(.+?):\s*(.+)$/);
            if (colonMatch) {
                return { name: colonMatch[1].trim(), type: colonMatch[2].trim() };
            }
            // Fallback to whitespace split
            const parts = trimmed.split(/\s+/);
            if (parts.length >= 2) {
                return { name: parts[0], type: parts.slice(1).join(' ') };
            }
            return { name: '', type: trimmed };
        }).filter(p => p.type);

        return { name, params, returnType: returnType?.trim() || 'VOID' };
    };

    const { params, returnType } = parseSignature(func.signature);
    const isCustom = func.source === 'custom';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-all" onClick={onClose} />

            <div className="relative bg-white dark:bg-gray-950 w-full max-w-2xl rounded-[32px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="px-8 py-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className={cn(
                            "p-2.5 rounded-xl shrink-0",
                            isCustom
                                ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400"
                                : "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                        )}>
                            {isCustom ? <Code size={20} strokeWidth={2.5} /> : <Database size={20} strokeWidth={2.5} />}
                        </div>
                        <div>
                            <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">
                                {func.name}
                            </h2>
                            <p className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                                {func.source} function • {func.language}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all text-gray-400"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 space-y-10 custom-scrollbar">
                    {error && (
                        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl text-[10px] font-black uppercase tracking-widest text-red-600 dark:text-red-400 flex items-center gap-3">
                            <AlertCircle size={14} />
                            {error}
                        </div>
                    )}

                    {/* Structured Signature Section (Read-only) */}
                    <section className="space-y-4">
                        <div className="flex items-center justify-between mb-4 px-1">
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Function Interface</h3>
                            <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 dark:bg-gray-900/50 px-2 py-0.5 rounded transition-all">Defined in {func.source}</span>
                        </div>

                        <div className="px-5 py-3 bg-gray-50/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 rounded-2xl overflow-x-auto custom-scrollbar">
                            <code className="text-[11px] font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                {func.signature}
                            </code>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-5 bg-gray-50/50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800">
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3 block">Inputs</span>
                                {params.length > 0 ? (
                                    <div className="space-y-2.5">
                                        {params.map((p, idx) => (
                                            <div key={idx} className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 font-mono">{p.name || `param${idx + 1}`}</span>
                                                <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/10 px-2 py-0.5 rounded uppercase tracking-tight">
                                                    {p.type}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-[10px] font-black text-gray-500 uppercase italic text-center block py-2">No parameters</span>
                                )}
                            </div>

                            <div className="p-5 bg-emerald-50/20 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100/50 dark:border-emerald-800/30 flex flex-col justify-between">
                                <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-3 block">Returns</span>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white dark:bg-gray-950 rounded-lg shadow-sm">
                                        <ArrowRight size={14} className="text-emerald-500" />
                                    </div>
                                    <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
                                        {returnType}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Description Section */}
                    <section className="space-y-4">
                        <div className="flex items-center justify-between mb-4 px-1">
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Documentation</h3>
                            <div className="flex items-center gap-3">
                                {!isEditingDescription ? (
                                    <>
                                        {func.source === 'bigquery' && (
                                            <button
                                                onClick={handleRegenerate}
                                                disabled={isRegenerating}
                                                className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                                            >
                                                {isRegenerating ? <Loader size={10} className="animate-spin" /> : <Sparkles size={10} />}
                                                Regenerate
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setIsEditingDescription(true)}
                                            className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700 transition-colors"
                                        >
                                            <Edit2 size={10} strokeWidth={3} />
                                            Edit
                                        </button>
                                    </>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => {
                                                setIsEditingDescription(false);
                                                setEditedDescription(displayUdf.description || '');
                                            }}
                                            className="text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveDescription}
                                            disabled={isSaving}
                                            className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700 transition-colors"
                                        >
                                            <Save size={10} />
                                            Save
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {isEditingDescription ? (
                            <textarea
                                value={editedDescription}
                                onChange={(e) => setEditedDescription(e.target.value)}
                                autoFocus
                                rows={5}
                                className="w-full px-5 py-4 bg-white dark:bg-gray-900 border-2 border-emerald-500 rounded-2xl text-xs font-medium leading-relaxed transition-all shadow-xl shadow-emerald-500/5 outline-none resize-none"
                                placeholder="Describe what this function does..."
                            />
                        ) : (
                            <div className="px-5 py-4 bg-gray-50/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 rounded-2xl">
                                <p className={cn(
                                    "text-xs font-medium leading-relaxed",
                                    !displayUdf.description && "text-gray-400 italic"
                                )}>
                                    {displayUdf.description || "No documentation provided."}
                                </p>
                            </div>
                        )}
                    </section>

                    {/* SQL Body Section */}
                    <section className="space-y-4">
                        <div className="flex items-center justify-between mb-4 px-1">
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">SQL Implementation</h3>
                            {isCustom ? (
                                !isEditingDefinition ? (
                                    <button
                                        onClick={() => setIsEditingDefinition(true)}
                                        className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700 transition-colors"
                                    >
                                        <Edit2 size={10} strokeWidth={3} />
                                        Edit Body
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => {
                                                setIsEditingDefinition(false);
                                                setEditedDefinition(displayUdf.definition || '');
                                            }}
                                            className="text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveDefinition}
                                            disabled={isSaving}
                                            className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700 transition-colors"
                                        >
                                            <Save size={10} />
                                            Save
                                        </button>
                                    </div>
                                )
                            ) : (
                                <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest bg-gray-100 dark:bg-gray-900/50 px-2 py-0.5 rounded">Source Only</span>
                            )}
                        </div>

                        {isLoading ? (
                            <div className="h-48 flex items-center justify-center bg-gray-50/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 rounded-2xl">
                                <div className="flex flex-col items-center gap-2">
                                    <Loader size={12} className="animate-spin text-emerald-500" />
                                    <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Fetching Code...</span>
                                </div>
                            </div>
                        ) : isEditingDefinition ? (
                            <textarea
                                value={editedDefinition}
                                onChange={(e) => setEditedDefinition(e.target.value)}
                                autoFocus
                                rows={10}
                                className="w-full px-5 py-4 bg-white dark:bg-gray-900 border-2 border-emerald-500 rounded-2xl font-mono text-[11px] leading-relaxed transition-all shadow-xl shadow-emerald-500/5 outline-none resize-none"
                                placeholder="Function body (SQL instructions)..."
                            />
                        ) : (
                            <div className="rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shadow-sm">
                                {displayUdf.definition ? (
                                    <SyntaxHighlighter
                                        language="sql"
                                        style={isDark ? vscDarkPlus : prism}
                                        customStyle={{
                                            margin: 0,
                                            padding: '1.5rem',
                                            fontSize: '11px',
                                            lineHeight: '1.6',
                                            background: 'transparent',
                                        }}
                                        codeTagProps={{
                                            style: {
                                                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                                            }
                                        }}
                                    >
                                        {displayUdf.definition}
                                    </SyntaxHighlighter>
                                ) : (
                                    <div className="p-10 flex flex-col items-center justify-center text-center opacity-40">
                                        <Terminal size={24} className="mb-2" />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Implementation Encrypted/Hidden</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    {/* BigQuery ID (Compact) */}
                    {displayUdf.routine_id && (
                        <section className="p-4 bg-blue-50/30 dark:bg-blue-900/5 rounded-2xl border border-blue-100/50 dark:border-blue-800/20">
                            <span className="text-[8px] font-black text-blue-400 uppercase tracking-widest mb-1.5 block">BigQuery ID Reference</span>
                            <code className="text-[10px] font-mono text-blue-600 dark:text-blue-400 break-all">
                                {displayUdf.routine_id}
                            </code>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FunctionDetailModal;
